import PropTypes from 'prop-types';
import ImmutablePropTypes from 'react-immutable-proptypes';

import {
  Header,
  HeaderItem,
  Sidebar,
  SidebarItem,
  SidebarPageWrapper,
  SidebarTabLabel,
  SidebarTab,
  SidebarTabs,
  SidebarFooter,
} from '@trueprodigy/components';

import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { connect, useSelector } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { compose, lifecycle, withStateHandlers } from 'recompose';

import styled from 'styled-components';
import HeaderZendeskWidget from '@components/HeaderZendeskWidget/HeaderZendeskWidget';
import SidebarRdsNameWidget from '@components/Sidebar/SidebarRdsNameWidget';
import SidebarCaseSettingWidget from '@components/Sidebar/SidebarCaseSettingWidget';
import SidebarAccountSettingsWidget from '@components/Sidebar/SidebarAccountSettingsWidget';
import HeaderWrapper from '@components/HeaderWrapper';
import QuickAccessAppeal from './QuickAccessAppeal';

import * as globalActions from '@actions/global';
import {
  isSidebarVisibleSelector,
  userRightsSelector,
  userStatusSelector,
} from '@selectors/global';
import { selectedAppealSelector } from '../selectors/global';
import { resetActiveRecords, selectAppealRecord } from '../actions/global';
import {
  searchAppeals,
  fetchCurrentAppealYear,
  fetchGeneralConfiguration,
} from '@modules/appeals/actions/appeals';
import {
  printCardSelector,
  appealConfigurationSelector,
} from '@modules/appeals/selectors/appeals';
import ability, { defineRules } from '@config/permissions/ability';
import OwnerLookupModal from '../../appraisal/components/ownerLookupModal';
import OwnerCreateModal from '../../appraisal/components/ownerCreateModal';
import PropertyLookupModal from '../../appraisal/components/propertyLookupModal';

import { AppealsRoutes } from '../constants';

import versionIcon from '../../../assets/images/PA_Version.png';
import screenIcon from '../../../assets/images/PA_Print-Screen.png';
import appealsLogo from '@assets/images/true-prodigy_appeals.png';
import rocketIcon from '@components/Sidebar/assets/rocket-icon.png';
import logIcon from '@assets/images/log-icon.png';

const parseHeaderTitle = ({ pathname, conferenceProperty }) => {
  if (pathname === AppealsRoutes.AppealsSearch) return 'Appeals Search';
  if (pathname === AppealsRoutes.AppealSummary) return 'Appeal Summary';
  if (
    pathname.includes(
      AppealsRoutes.BoardMemberMaintenance.replace(':memberId?', ''),
    )
  ) {
    return 'Board Member Maintenance';
  }
  if (
    pathname.includes(AppealsRoutes.PanelMaintenance.replace(':panelId?', ''))
  ) {
    return 'Panel Maintenance';
  }
  if (
    pathname.includes(AppealsRoutes.DocketLocations.replace(':locationId?', ''))
  ) {
    return 'Docket Locations Maintenance';
  }
  if (
    pathname.includes(AppealsRoutes.StatusCodes.replace(':appealStatusId?', ''))
  ) {
    return 'Status Codes';
  }
  if (pathname === AppealsRoutes.WaitingRoomFormal)
    return 'Waiting Rooms - Formal';
  if (pathname === AppealsRoutes.WaitingRoomInformal)
    return 'Waiting Rooms - Informal';
  if (pathname === AppealsRoutes.PanelOverview) return 'Panel Overview';
  if (pathname === AppealsRoutes.ManagerOverview) return 'Manager Overview';
  if (pathname === AppealsRoutes.AppraiserOverview) return 'Appraiser Overview';
  if (pathname === AppealsRoutes.PanelView) return 'Panel View';
  if (pathname === AppealsRoutes.Dashboard) return 'Appeals Dashboard';
  if (pathname.startsWith(AppealsRoutes.Meeting)) return 'Appeal Meeting';
  if (pathname.startsWith(AppealsRoutes.TaxpayerPresentationView))
    return `Tax Payer Presentation - Property ID: ${conferenceProperty}`;
  if (pathname.startsWith(AppealsRoutes.CadPresentationView))
    return `CAD Presentation - Property ID: ${conferenceProperty}`;
  if (pathname === AppealsRoutes.PanelStatisticsView) return 'Panel Statistics';
  return '';
};

const parseHelmetTitle = ({ pathname, conferenceProperty }) => {
  if (pathname.startsWith(AppealsRoutes.TaxpayerPresentationView))
    return `TaxPayer Presentation - ${conferenceProperty}`;
  if (pathname.startsWith(AppealsRoutes.CadPresentationView))
    return `CAD Presentation - ${conferenceProperty}`;
  return 'Appeals';
};

const PageWrapper = ({
  activeAppeal,
  activeTab,
  children,
  closeTab,
  history,
  isSidebarVisible,
  location,
  setActiveTab,
  searchAppeals,
  selectAppealRecord,
  toggleSidebar,
  userRights,
  printCard,
  userStatus,
}) => {
  const appealConfiguration = useSelector(appealConfigurationSelector);
  const isReadOnly = userStatus.get('readOnly');

  const [conferenceProperty, setConferenceProperty] = useState(null);

  const onStorageUpdate = (e) => {
    const { key, newValue } = e;
    if (key === 'conferenceActiveProperty') {
      setConferenceProperty(newValue);
    }
  };

  useEffect(() => {
    setConferenceProperty(localStorage.getItem('conferenceActiveProperty'));

    window.addEventListener('storage', onStorageUpdate);
    return () => {
      window.removeEventListener('storage', onStorageUpdate);
    };
  }, []);

  useEffect(() => {
    if (userRights && userRights.toJS() && userRights.toJS().length) {
      const rules = defineRules(userRights.toJS());
      ability.update(rules);
    }
  }, [userRights]);

  useEffect(() => {
    const isiPad = navigator.userAgent.match(/Mac/) && navigator.maxTouchPoints;
    if (isSidebarVisible && isiPad) {
      toggleSidebar();
    }
  }, [location.pathname]);

  return (
    <>
      <Helmet>
        <title>{parseHelmetTitle({ ...location, conferenceProperty })}</title>
      </Helmet>
      <QuickAccessAppeal
        history={history}
        quickAppealLookup={searchAppeals}
        selectAppealRecord={selectAppealRecord}
      />
      <Sidebar
        isSidebarVisible={isSidebarVisible}
        logo={appealsLogo}
        sidebarFooter={
          <SidebarFooter>
            <SidebarRdsNameWidget />
            <SidebarCaseSettingWidget />
            <SidebarAccountSettingsWidget />
          </SidebarFooter>
        }
      >
        <SidebarItem icon={rocketIcon} link="/" title="Prodigy App Launcher" />
        <SidebarTabs
          indicatorColor="secondary"
          scrollButtons="off"
          textColor="primary"
          value={activeTab}
          variant="fullWidth"
          onChange={setActiveTab}
        >
          <SidebarTab
            label={
              <SidebarTabLabel isActive={activeTab === 0} label="Appeals" />
            }
          />
          {activeAppeal && (
            <SidebarTab
              label={
                <SidebarTabLabel
                  hasClose
                  isActive={activeTab === 1}
                  label="Appeal"
                  onClose={(event) => {
                    event.stopPropagation();
                    closeTab();
                    history.push(AppealsRoutes.AppealsSearch);
                  }}
                />
              }
            />
          )}
        </SidebarTabs>
        {activeTab === 0 && (
          <>
            <SidebarItem link={AppealsRoutes.Dashboard} title="Dashboard" />
            {/* <SidebarItem
              link={AppealsRoutes.AppealsSearch}
              title="Appeal Search"
            /> */}
            <SidebarItem
              link={AppealsRoutes.AppealsSearch}
              title="Appeal Search"
            />
            {appealConfiguration.useWaitingRoom !== 0 && (
              <>
                <SidebarItem
                  link={AppealsRoutes.WaitingRoomFormal}
                  title="Waiting Rooms - Formal"
                />
                <SidebarItem
                  link={AppealsRoutes.WaitingRoomInformal}
                  title="Waiting Rooms - Informal"
                />
                <SidebarItem
                  link={AppealsRoutes.PanelOverview}
                  title="Panel Overview"
                />
                <SidebarItem
                  link={AppealsRoutes.PanelStatisticsView}
                  title="Panel Statistics"
                />
                {/* <SidebarItem
                  link={AppealsRoutes.ManagerOverview}
                  title="Manager Overview"
                /> */}
                <SidebarItem
                  link={AppealsRoutes.AppraiserOverview}
                  title="Appraiser Overview"
                />
              </>
            )}
            <SidebarItem link={AppealsRoutes.DocketsSearch} title="Dockets" />
            {isReadOnly ? (
              <></>
            ) : (
              <SidebarItem
                link={AppealsRoutes.CreateAppealWalkthru.replace(
                  ':propertyId?',
                  '',
                )}
                title="Create Appeal"
              />
            )}

            {isReadOnly ? (
              <></>
            ) : (
              <SidebarItem
                link={AppealsRoutes.MassCreateAppeal}
                title="Mass Create Appeal"
              />
            )}
            {isReadOnly ? (
              <></>
            ) : (
              <SidebarItem nested title="Imports">
                <SidebarItem
                  link={AppealsRoutes.InformalMeetingImport}
                  title="Informal Meeting Import"
                />
                <SidebarItem
                  link={AppealsRoutes.FormalMeetingImport}
                  title="Formal Hearing Import"
                />
              </SidebarItem>
            )}

            {isReadOnly ? (
              <></>
            ) : (
              <SidebarItem nested title="Agent Processes">
                <SidebarItem
                  link={AppealsRoutes.AgentMassDownloadValidation}
                  title="Agent Mass Download Validation"
                />
                <SidebarItem
                  link={AppealsRoutes.AgentDocketGroupSearch}
                  title="Agent Docket Groups"
                />
                <SidebarItem
                  link={AppealsRoutes.MassAoaHistory}
                  title="Agent AOA Maintenance"
                />
                <SidebarItem
                  link={AppealsRoutes.ResetPrimaryProtester}
                  title="Reset Primary Protestor"
                />
                <SidebarItem
                  link={AppealsRoutes.AddAuthorizedAgentProtest}
                  title="Add Authorized Agent to Protest"
                />
              </SidebarItem>
            )}
            <SidebarItem nested title="History">
              <SidebarItem
                link={AppealsRoutes.FinalLetterHistory}
                title="Final Letter Creation"
              />
              <SidebarItem
                link={AppealsRoutes.MassLetterHistory}
                title="Mass Appeal Letter History"
              />
              <SidebarItem
                link={AppealsRoutes.MassFinalizeHistory}
                title="Mass Finalize Appeal History"
              />
              <SidebarItem
                link={AppealsRoutes.MassProcessHistory}
                title="Mass Process Appeal History"
              />
              <SidebarItem
                link={AppealsRoutes.MassAttachHistory}
                title="Mass Attach File History"
              />
              <SidebarItem
                link={AppealsRoutes.EvidenceUploadHistory}
                title="Mass Evidence Upload History"
              />
              <SidebarItem
                link={AppealsRoutes.MassDownloadHistory}
                title="Mass Download History"
              />
            </SidebarItem>
            {isReadOnly ? (
              <></>
            ) : (
              <SidebarItem nested title="Settings">
                <SidebarItem
                  link={AppealsRoutes.PanelMaintenance.replace(':panelId?', '')}
                  title="Panel Maintenance"
                />
                <SidebarItem
                  link={AppealsRoutes.BoardMemberMaintenance.replace(
                    ':memberId?',
                    '',
                  )}
                  title="Board Member Maintenance"
                />
                <SidebarItem
                  link={AppealsRoutes.StatusCodes.replace(
                    ':appealStatusId?',
                    '',
                  )}
                  title="Status Codes"
                />
                <SidebarItem
                  link={AppealsRoutes.DocketLocations.replace(
                    ':locationId?',
                    '',
                  )}
                  title="Docket Locations"
                />
                <SidebarItem
                  link={AppealsRoutes.AppealConfigurations}
                  title="Configurations"
                />
                <SidebarItem
                  link={AppealsRoutes.EletronicDeliveryNoticeSetting}
                  title="Electronic Delivery Notice Settings"
                />
              </SidebarItem>
            )}
            {isReadOnly ? (
              <></>
            ) : (
              <SidebarItem nested title="Commercial Evidence Packets">
                <SidebarItem
                  link={AppealsRoutes.AppealsCommercialArtifacts}
                  title="Artifacts"
                />
                <SidebarItem
                  link={AppealsRoutes.AppealsCommercialComponents}
                  title="Property Components"
                />
                <SidebarItem
                  link={AppealsRoutes.AppealsCommercialConfiguration}
                  title="Configuration"
                />
              </SidebarItem>
            )}
          </>
        )}
        {activeTab === 1 && activeAppeal && (
          <>
            <SidebarItem
              link={AppealsRoutes.AppealSummary.replace(':id', activeAppeal)}
              title="Summary"
            />
          </>
        )}
      </Sidebar>
      <PageWrapper.ContentWrapper>
        <SidebarPageWrapper isSidebarVisible={isSidebarVisible}>
          <HeaderWrapper>
            <Header
              title={parseHeaderTitle({
                ...location,
                ...activeAppeal,
                conferenceProperty,
              })}
              toggleSidebar={toggleSidebar}
            >
              {printCard && printCard.toJS() && printCard.toJS().data && (
                <div style={{ width: 156 }} className="recalculating">{`${
                  printCard.toJS().data
                }…`}</div>
              )}
              <HeaderItem
                icon={screenIcon}
                title="Print Screen"
                onClick={() => window.print()}
              />
              <HeaderItem
                icon={versionIcon}
                title="Smart List"
                onClick={() =>
                  history.push('/appeals/smart-lists?module=Prodigy%20Appeals')
                }
              />
              <HeaderItem icon={logIcon} title="Job Log" onClick={() => {}} />
              <HeaderZendeskWidget />
            </Header>
          </HeaderWrapper>

          <div>{children}</div>
        </SidebarPageWrapper>
      </PageWrapper.ContentWrapper>

      <OwnerLookupModal />
      <OwnerCreateModal />
      <PropertyLookupModal />
    </>
  );
};

PageWrapper.propTypes = {
  activeAppeal: PropTypes.number,
  activeTab: PropTypes.number.isRequired,
  children: PropTypes.node,
  closeTab: PropTypes.func.isRequired,
  history: PropTypes.object.isRequired,
  isSidebarVisible: PropTypes.bool,
  location: PropTypes.object.isRequired,
  resetActiveRecords: PropTypes.func.isRequired,
  searchAppeals: PropTypes.func.isRequired,
  setActiveTab: PropTypes.func.isRequired,
  selectAppealRecord: PropTypes.func.isRequired,
  toggleSidebar: PropTypes.func.isRequired,
  userRights: ImmutablePropTypes.list,
  printCard: PropTypes.any,
  userStatus: PropTypes.any,
};

PageWrapper.ContentWrapper = styled.div`
  > div {
    margin-top: -67px;
    min-height: 100vh;
    background-color: #f2f4f6;
  }
`;

const mapStateToProps = (state) => ({
  activeAppeal: selectedAppealSelector(state),
  isSidebarVisible: isSidebarVisibleSelector(state),
  userRights: userRightsSelector(state),
  printCard: printCardSelector(state),
  userStatus: userStatusSelector(state),
});

const mapDispatchToProps = {
  toggleSidebar: globalActions.toggleSidebar,
  resetActiveRecords,
  selectAppealRecord,
  searchAppeals,
  fetchCurrentAppealYear,
  fetchGeneralConfiguration,
};

const enhance = compose(
  withRouter,
  connect(mapStateToProps, mapDispatchToProps),
  withStateHandlers(({ activeTab = 0 }) => ({ activeTab }), {
    setActiveTab: () => (_, activeTab) => ({ activeTab }),
    closeTab:
      (_, { resetActiveRecords }) =>
      () => {
        resetActiveRecords();
        return { activeTab: 0 };
      },
  }),
  lifecycle({
    componentDidMount() {
      this.props.setActiveTab(null, 0);
      this.props.fetchCurrentAppealYear();
      this.props.fetchGeneralConfiguration();
    },
    componentDidUpdate(prevProps) {
      if (
        this.props.activeAppeal !== prevProps.activeAppeal &&
        this.props.activeAppeal
      ) {
        this.props.setActiveTab(null, 1);
      }
    },
  }),
);

export default enhance(PageWrapper);
